import { store } from "@/_store";
import Vue from "vue";
import VueRouter from "vue-router";
import AdminReport from "../views/blog/AdminReport.vue";
import Blog from "../views/blog/Blog.vue";
import CreateReport from "../views/blog/CreateReport.vue";
import Disclaimer from "../views/Disclaimer.vue";
import Habeas from "../views/HabeasData.vue";
import Home from "../views/Home.vue";
import Landing from "../views/Landing.vue";
import Login from "../views/users/Login.vue";
import Callback from "../views/users/Callback.vue";
import Register from "../views/users/Register.vue";
import Report from "../views/blog/Report.vue";
import ResetPassword from "../views/users/ResetPassword.vue";
import SetPassword from "../views/users/SetPassword.vue";
import { jwtDecode } from "jwt-decode";
import { tokenService } from "@/_services/token.service";
import Overview from "../views/Overview.vue";
import Analytics from "../views/Analytics.vue";
import DataModel from "../views/DataModel.vue";
import { makeRoute } from "./helper";

Vue.use(VueRouter);

/**
 * User roles
 */

const routesAnalytics = [
  "analytics-valuation",
  "analytics-management",
  "analytics-management-strategies",
  "analytics-management-contracts",
  "analytics-management-date_2",
  "analytics-management-clients",
  "analytics-management-suppliers",
  "analytics-scenarios",
  "analytics-scenarios-portfolio",
  "analytics-scenarios-strategies",
  "analytics-scenarios-derivatives",
  "analytics-scenarios-providers",
  "analytics-scenarios-clients",
  "analytics-scenarios-counterparty"
];

const routesAdvanced = [
  "advanced-scenario-analysis",
  "advanced-historical-analysis"
];
const routesMarkets = ["markets-global", "markets-analysis"];
const routesAnalysis = [
  "markets",
  ...routesMarkets,
  "analytics",
  "advanced",
  "optimizer",
  "data",
  "data-exposures",
  "data-hedges",
  "data-hedging-program",
  "data-upload",
  "overview",
  "overview-data-table",
  "overview-charts-layout",
  "overview-forwards-volatility",
  "overview-commodities",
  "home",
  "hedge",
  ...routesAnalytics,
  ...routesAdvanced
];
const managementSubviews = [
  { path: "strategies", name: "analytics-management-strategies" },
  { path: "contracts", name: "analytics-management-contracts" },
  { path: "date", name: "analytics-management-date_2" },
  { path: "clients", name: "analytics-management-clients" },
  { path: "suppliers", name: "analytics-management-suppliers" }
];

const scenariosSubviews = [
  { path: "portfolio", name: "analytics-scenarios-portfolio" },
  { path: "strategies", name: "analytics-scenarios-strategies" },
  { path: "derivatives", name: "analytics-scenarios-derivatives" },
  { path: "suppliers", name: "analytics-scenarios-providers" },
  { path: "clients", name: "analytics-scenarios-clients" },
  { path: "counterparty", name: "analytics-scenarios-counterparty" }
];

const staffRoutes = ["docs", "staffDocs", "prds"];
const routesHedge = ["analyticsBeta", "supervisor", "trader"];
// const routesTrader = ['trader'];
const routesTestedModules = ["multi-asset"];
const routesOptimizer = ["home", "optimizer", "overview"];
const routesAnalysisHedge = [...routesAnalysis, ...routesHedge];
const routesAdmin = [
  ...routesAnalysisHedge,
  ...routesTestedModules,
  ...staffRoutes
];
const roleRoutes = {
  1: {
    routesAllowed: routesAdmin
  },
  2: {
    routesAllowed: routesAnalysis
  },
  3: {
    routesAllowed: routesHedge
  },
  5: {
    routesAllowed: routesOptimizer
  }
};

function resolveRoleRoute(to, next) {
  const items =
    store.state.user && store.state.user.all && store.state.user.all.items;
  const roleConfig = items && roleRoutes[items.platform_role];
  if (!roleConfig) {
    store.dispatch("authentication/logout");
    return next({ name: "login" });
  }
  if (roleConfig.routesAllowed.includes(to.name)) {
    return next();
  }
  return next({ name: roleConfig.routesAllowed[0] });
}

const routes = [
  {
    path: "/",
    redirect: "/overview/data-table" // Redirect root to default overview subview
  },
  {
    path: "/login",
    name: "login",
    component: Login
  },
  {
    path: "/register",
    name: "register",
    component: Register
  },
  {
    path: "/oidc/callback/",
    name: "Callback",
    component: Callback
  },
  {
    path: "/reset_password",
    name: "reset_password",
    component: ResetPassword
  },
  {
    path: "/set_password/:id",
    name: "set_password",
    component: SetPassword
  },
  {
    path: "/profile",
    name: "profile",
    component: () =>
      import(/* webpackChunkName: "profile" */ "../views/Profile.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/data/:productType?",
    name: "data",
    component: DataModel,
    meta: { requiresAuth: true },
    props: route => ({
      productType: route.params.productType || null
    }),
    children: [
      { path: "exposures", name: "data-exposures", component: DataModel },
      { path: "hedges", name: "data-hedges", component: DataModel },
      {
        path: "hedging-program",
        name: "data-hedging-program",
        component: DataModel
      },
      { path: "upload", name: "data-upload", component: DataModel }
    ],
    beforeEnter(to, from, next) {
      resolveRoleRoute(to, next);
    }
  },
  {
    path: "/overview",
    name: "overview",
    component: () =>
      import(/* webpackChunkName: "overview" */ "../views/Overview.vue"),
    meta: { requiresAuth: true },
    children: [
      {
        path: "data-table",
        name: "overview-data-table",
        component: Overview,
        props: { subview: "data-table" }
      },
      {
        path: "charts-layout",
        name: "overview-charts-layout",
        component: Overview,
        props: { subview: "charts-layout" }
      },
      {
        path: "forwards-volatility/:currency?",
        name: "overview-forwards-volatility",
        component: Overview,
        props: route => ({
          subview: "forwards-volatility",
          currency: route.params.currency
        })
      },
      {
        path: "commodities",
        name: "overview-commodities",
        component: Overview,
        props: route => ({
          subview: "commodities",
          commodity1: route.query.commodity1,
          commodity2: route.query.commodity2
        })
      }
    ],
    beforeEnter(to, from, next) {
      resolveRoleRoute(to, next);
    }
  },
  {
    path: "/analytics/:productType?",
    name: "analytics",
    component: () =>
      import(/* webpackChunkName: "analytics" */ "../views/Analytics.vue"),
    meta: { requiresAuth: true },
    props: route => ({
      productType: route.params.productType || null
    }),
    children: [
      makeRoute("valuation", "analytics-valuation", "valuation"),
      {
        path: "management",
        name: "analytics-management",
        component: Analytics,
        children: managementSubviews.map(s => makeRoute(s.path, s.name, s.path))
      },
      {
        path: "analysis",
        name: "analytics-scenarios",
        component: Analytics,
        children: scenariosSubviews.map(s => makeRoute(s.path, s.name, s.path))
      }
    ],
    beforeEnter(to, from, next) {
      resolveRoleRoute(to, next);
    }
  },
  {
    path: "/advanced/:productType?",
    name: "advanced",
    component: () =>
      import(/* webpackChunkName: "commodity" */ "../views/Advanced.vue"),
    meta: { requiresAuth: true },
    props: route => ({
      productType: route.params.productType || null
    }),
    children: [
      makeRoute(
        "scenario-analysis",
        "advanced-scenario-analysis",
        "scenario-analysis"
      ),
      makeRoute(
        "historical-analysis",
        "advanced-historical-analysis",
        "historical-analysis"
      )
    ],
    beforeEnter(to, from, next) {
      resolveRoleRoute(to, next);
    }
  },
  {
    path: "/multi-asset",
    name: "multi-asset",
    component: () =>
      import(/* webpackChunkName: "multi-asset" */ "../views/MultiAsset.vue"),
    meta: { requiresAuth: true },
    beforeEnter(to, from, next) {
      resolveRoleRoute(to, next);
    }
  },
  {
    path: "/home",
    name: "home",
    component: Home,
    meta: { requiresAuth: true },
    beforeEnter(to, from, next) {
      resolveRoleRoute(to, next);
    }
  },
  {
    path: "/optimizer",
    name: "optimizer",
    component: () =>
      import(/* webpackChunkName: "optimizer" */ "../views/Optimizer.vue"),
    meta: { requiresAuth: true },
    beforeEnter(to, from, next) {
      resolveRoleRoute(to, next);
    }
  },
  {
    path: "/markets/:productType?",
    name: "markets",
    component: () =>
      import(/* webpackChunkName: "markets" */ "../views/Markets.vue"),
    meta: {
      requiresAuth: true
    },
    children: [
      {
        // Vistas no atadas a un producto (world-currencies, dot-plot, ...)
        path: ":analysisType",
        name: "markets-global",
        component: () =>
          import(/* webpackChunkName: "markets" */ "../views/Markets.vue")
      },
      {
        path: ":productItem/:analysisType",
        name: "markets-analysis",
        component: () =>
          import(/* webpackChunkName: "markets" */ "../views/Markets.vue")
      }
    ],
    beforeEnter(to, from, next) {
      resolveRoleRoute(to, next);
    }
  },
  {
    path: "/hedge",
    name: "hedge",
    component: () => import("../views/Hedge.vue"),
    meta: { requiresAuth: true },
    beforeEnter(to, from, next) {
      resolveRoleRoute(to, next);
    }
  },
  {
    path: "/supervisor",
    name: "supervisor",
    component: () => import("../views/Supervisor.vue"),
    meta: { requiresAuth: true },
    beforeEnter(to, from, next) {
      resolveRoleRoute(to, next);
    }
  },
  {
    path: "/trader",
    name: "trader",
    component: () => import("../views/Trader.vue"),
    meta: { requiresAuth: true },
    beforeEnter(to, from, next) {
      resolveRoleRoute(to, next);
    }
  },
  {
    path: "/docs",
    name: "docs",
    component: () => import("../views/documentation/Documentation"),
    meta: { requiresAuth: true },
    beforeEnter(to, from, next) {
      resolveRoleRoute(to, next);
    }
  },
  {
    path: "/staffDocs",
    name: "staffDocs",
    component: () => import("../views/documentation/StaffDocumentation"),
    meta: { requiresAuth: true },
    beforeEnter(to, from, next) {
      resolveRoleRoute(to, next);
    }
  },
  {
    path: "/reports",
    name: "reports",
    component: () =>
      import(/* webpackChunkName: "reports" */ "../views/Reports.vue"),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: "/tutorials",
    name: "tutorials",
    component: () =>
      import(/* webpackChunkName: "tutorials" */ "../views/Tutorials.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/tools",
    name: "tools",
    component: () =>
      import(/* webpackChunkName: "tools" */ "../views/Tools.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/disclaimer",
    name: "disclaimer",
    component: Disclaimer
  },
  {
    path: "/edit-user",
    name: "edituser",
    component: () =>
      import(/* webpackChunkName: "edituser" */ "../views/EditUser.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/Habeas",
    name: "Habeas",
    component: Habeas,
    meta: { requiresAuth: true }
  },
  {
    path: "/ConfirmRessetPassword",
    name: "ConfirmRessetPassword",
    component: () =>
      import(
        /* webpackChunkName: "ConfirmRessetPassword" */ "../views/ConfirmRessetPassword.vue"
      )
  },
  {
    path: "/CreateReport/",
    name: "CreateReport",
    component: CreateReport,
    meta: { requiresAuth: true }
  },
  {
    path: "/EditReport/:id",
    name: "EditReport",
    component: CreateReport,
    meta: { requiresAuth: true }
  },
  {
    path: "/AdminReport/",
    name: "AdminReport",
    component: AdminReport,
    meta: { requiresAuth: true }
  },
  {
    path: "/blog",
    name: "blog",
    component: Blog
  },
  {
    path: "/report/:id",
    name: "report",
    component: Report
  },
  {
    path: "/landing/:lang?",
    name: "landing",
    component: Landing
  },
  // otherwise redirect to home
  { path: "*", redirect: "/" }
];

const router = new VueRouter({
  mode: "history",
  // base: process.env.BASE_URL,
  routes
});

router.beforeEach(async (to, from, next) => {
  let token = localStorage.getItem("token");

  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const isExpired = Date.now() > decoded.exp * 1000;

        if (isExpired) {
          try {
            // Attempt silent refresh before giving up
            token = await tokenService.refreshAccessToken();
          } catch {
            store.dispatch("authentication/logout");
            return next({ name: "login" });
          }
        }
      } catch (error) {
        store.dispatch("authentication/logout");
        console.log({ error });
        return next({ name: "login" });
      }

      const user = store.state.user.all.items;
      if (!user.habeas_data || !user.is_active) {
        if (to.path !== "/edit-user") {
          next({ name: "edituser" });
        } else {
          next();
        }
        return;
      } else {
        next();
        return;
      }
    }
    next("/landing/es");
  } else {
    next();
  }
});

export default router;
